import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { workforceApi } from '../../finance/api/workforce';
import { FiUsers, FiMonitor, FiCoffee, FiUserCheck, FiUserX, FiRefreshCw } from 'react-icons/fi';

export default function TeamPresence() {
  const { data: team, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['teamPresence'],
    queryFn: workforceApi.getTeamPresence,
  });

  const getStatusConfig = (status) => {
    switch (status) {
      case 'AVAILABLE': return { icon: FiUserCheck, color: 'text-green-500', bg: 'bg-green-500/10', border: 'border-green-500/20' };
      case 'IN_MEETING': return { icon: FiMonitor, color: 'text-yellow-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' };
      case 'BREAK': return { icon: FiCoffee, color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/20' };
      default: return { icon: FiUserX, color: 'text-slate-500', bg: 'bg-slate-500/10', border: 'border-slate-500/20' };
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in pb-12">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FiUsers className="text-blue-600" /> Team Presence
          </h1>
          <p className="text-slate-500 text-sm mt-1">Live overview of your team's current work status</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-md transition-colors"
        >
          <FiRefreshCw className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-100 animate-pulse rounded-lg"></div>)}
        </div>
      ) : (!Array.isArray(team) || team.length === 0) ? (
        <div className="col-span-full py-12 text-center text-slate-500 bg-white rounded-lg border border-slate-200">
          <FiUsers className="mx-auto text-4xl text-slate-300 mb-3" />
          <p>No presence data available yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {team?.map(member => {
            const config = getStatusConfig(member.status);
            const Icon = config.icon;
            
            return (
              <div key={member.userId} className={`bg-white rounded-lg border ${config.border} p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden`}>
                {member.workMode === 'WFH' && (
                  <div className="absolute top-0 right-0 bg-purple-500 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg">
                    WFH
                  </div>
                )}
                
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full ${config.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`text-xl ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-slate-800 truncate">{member.employeeName || 'Unknown'}</h3>
                    <div className={`text-xs font-semibold ${config.color} mt-0.5`}>
                      {(member.status || 'OFFLINE').replace('_', ' ')}
                    </div>
                    {member.lastCheckIn && (
                      <div className="text-[10px] text-slate-400 mt-1">
                        Since {new Date(member.lastCheckIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          
          {(!team || team.length === 0) && (
            <div className="col-span-full py-12 text-center text-slate-500 bg-white rounded-lg border border-slate-200">
              <FiUsers className="mx-auto text-4xl text-slate-300 mb-3" />
              <p>No presence data available yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
